import { useEffect, useMemo } from 'react';

import {
	type DevDocsBylineConnectionAncestor,
	type DevDocsBylineConnection,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { fg } from '@confluence/feature-gating';

import { useAtAGlance } from '../at-a-glance/useAtAGlance.ts';
import { clearAtAGlance, publishAtAGlance } from '../store/atAGlanceBridge.ts';
import {
	clearDevDocsConnection,
	publishDevDocsConnection,
} from '../store/devDocsConnectionBridge.ts';
import { clearIncomingLinks, publishIncomingLinks } from '../store/incomingLinksBridge.ts';
import { clearOutgoingLinks, publishOutgoingLinks } from '../store/outgoingLinksBridge.ts';
import { clearRelatedContent, publishRelatedContent } from '../store/relatedContentBridge.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

import { useIncomingLinks, type UseIncomingLinksResult } from './useIncomingLinks.ts';
import { useRelatedContent, type UseRelatedContentResult } from './useRelatedContent.ts';
import { useSmartLinks, type UseSmartLinksResult } from './useSmartLinks.ts';

export type BylineConnectionsDataOptions = {
	atAGlanceEnabled: boolean;
	contentId?: string;
	contentType?: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageTitle?: string | null;
};

type BylineConnectionsData = {
	outgoing: UseSmartLinksResult;
	incoming: UseIncomingLinksResult;
	relatedContent: UseRelatedContentResult;
	devDocsConnection: DevDocsBylineConnection | null;
};

/** Sole fetching owner, mounted by the byline under its SmartCard provider. */
export const useBylineConnectionsData = ({
	atAGlanceEnabled,
	contentId,
	contentType,
	connectionAncestors,
	pageVersion,
	pageUpdatedAt,
	pageTitle,
}: BylineConnectionsDataOptions): BylineConnectionsData => {
	const outgoing = useSmartLinks(contentId);
	const {
		links: incomingLinks,
		twgLinks: incomingTwgLinks,
		loading,
		settled,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error,
	} = useIncomingLinks(contentId, { contentType });
	// Keep bridge snapshots stable when unrelated byline or AI state changes.
	const incoming = useMemo(
		() => ({
			links: incomingLinks,
			twgLinks: incomingTwgLinks,
			loading,
			settled,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			error,
		}),
		[
			incomingLinks,
			incomingTwgLinks,
			loading,
			settled,
			loadingMore,
			hasNextPage,
			endCursor,
			loadMore,
			error,
		],
	);
	const {
		enabled,
		links: relatedLinks,
		loading: relatedLoading,
		error: relatedError,
	} = useRelatedContent(contentId, { contentType });
	const relatedContent = useMemo(
		() => ({ enabled, links: relatedLinks, loading: relatedLoading, error: relatedError }),
		[enabled, relatedLinks, relatedLoading, relatedError],
	);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const atAGlance = useAtAGlance({
		// cc-smarts checks this same tenant-keyed kill switch before generating, so reusing it
		// keeps the request and the service enabled for the same tenants. A frontend-only
		// experiment would allocate its own audience and leave some tenants asking a service
		// that refuses them.
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		enabled: atAGlanceEnabled && fg('cc_smarts_twg_at_a_glance_enabled'),
		contentId,
		contentType,
		pageTitle,
		pageAdf: outgoing.collectedForContentId === contentId ? outgoing.collectedAdf : null,
		pageVersion,
		pageUpdatedAt,
		structuralAncestors: connectionAncestors,
		outgoingLinksLoading: outgoing.loading || outgoing.refreshing,
		// useAtAGlance also waits for incoming.loading via appearsIn and for
		// enabled Related content to settle before the first request.
		connections: {
			appearsIn: incoming,
			linksTo: { links: outgoing.links },
			relatedContent,
		},
	});

	// The preview panel mounts outside this subtree and reads the bridge.
	// Tag links with their collected-for id to keep page transitions consistent.
	useEffect(() => {
		publishOutgoingLinks({
			contentId: outgoing.collectedForContentId,
			outgoingLinks: outgoing.links,
		});
	}, [outgoing.collectedForContentId, outgoing.links]);

	// Separate from the publish effect so the store is only emptied when the
	// byline actually goes away, not on every republish.
	useEffect(() => clearOutgoingLinks, []);

	// Tagged with `contentId` rather than a collected-for id because
	// `connectionAncestors` arrives as a prop alongside it, so the two move together.
	useEffect(() => {
		publishDevDocsConnection({ contentId, devDocsConnection });
	}, [contentId, devDocsConnection]);

	useEffect(() => clearDevDocsConnection, []);

	useEffect(() => {
		publishIncomingLinks({ contentId, incomingLinks: incoming });
	}, [contentId, incoming]);
	useEffect(() => clearIncomingLinks, []);

	useEffect(() => {
		publishRelatedContent({ contentId, relatedContent });
	}, [contentId, relatedContent]);
	useEffect(() => clearRelatedContent, []);

	useEffect(() => {
		publishAtAGlance({ contentId, atAGlance });
	}, [contentId, atAGlance]);
	useEffect(() => clearAtAGlance, []);

	return { outgoing, incoming, relatedContent, devDocsConnection };
};
